import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Start', to: '/' },
  { label: 'Love Code', to: '/seelenpartner-love-code' },
  { label: 'Business Code', to: '/female-leadership-fuer-frauen' },
  { label: 'Shop', to: '/shop' },
];

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e) => {
    setMousePos({
      x: (e.clientX / window.innerWidth) * 100,
      y: (e.clientY / window.innerHeight) * 100,
    });
  };

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex flex-col overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Nav */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="relative z-50 flex items-center justify-between px-8 py-5 border-b border-white/5 bg-dark/60 backdrop-blur-xl"
      >
        <Link to="/" className="text-gold font-serif text-xl tracking-widest"
          style={{ textShadow: '0 0 15px rgba(212,175,55,0.6)' }}>
          CF.
        </Link>
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            <Link key={l.label} to={l.to}
              className="text-xs uppercase tracking-widest text-cream/55 hover:text-gold transition-colors duration-500">
              {l.label}
            </Link>
          ))}
        </div>
      </motion.nav>

      {/* BG */}
      <motion.div style={{ y }} className="absolute inset-0 z-0 scale-110">
        <div className="absolute inset-0 bg-dark" />
        <div
          className="absolute inset-0 transition-all duration-300 pointer-events-none"
          style={{
            background: `radial-gradient(800px circle at ${mousePos.x}% ${mousePos.y}%, rgba(212,175,55,0.07) 0%, transparent 60%)`,
          }}
        />
        <motion.div
          animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.1, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.08) 0%, transparent 60%)' }}
        />
        <motion.div
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 25% 75%, rgba(114,47,55,0.12) 0%, transparent 50%)' }}
        />
        {[...Array(6)].map((_, i) => (
          <motion.div key={i} className="absolute top-0 bottom-0 w-[1px]"
            style={{
              left: `${15 + i * 14}%`,
              background: i % 2 === 0
                ? 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.07), transparent)'
                : 'linear-gradient(to bottom, transparent, rgba(114,47,55,0.05), transparent)',
              filter: 'blur(1px)',
            }}
            animate={{ opacity: [0, 0.8, 0], scaleY: [0.5, 1, 0.5] }}
            transition={{ duration: 5 + i * 1.2, repeat: Infinity, delay: i * 0.8 }}
          />
        ))}
        {[...Array(14)].map((_, i) => (
          <motion.div key={i} className="absolute rounded-full bg-gold pointer-events-none"
            style={{ width: Math.random() * 3 + 1, height: Math.random() * 3 + 1, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
            animate={{ y: [-25, 25, -25], opacity: [0.05, 0.4, 0.05] }}
            transition={{ duration: 5 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 4 }}
          />
        ))}
        <div className="absolute inset-0 opacity-[0.025]"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '200px' }}
        />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity }}
        className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-24"
      >
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.05em' }}
          animate={{ opacity: 1, letterSpacing: '0.45em' }}
          transition={{ duration: 2, delay: 0.3 }}
          className="text-gold text-xs uppercase font-semibold mb-8"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.8)' }}
        >
          Kostenfreie Live-Masterclass · Love Shift & Feminine Business
        </motion.p>

        <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif leading-tight mb-8">
          {['Was', 'wäre', 'wenn', 'du', 'aufhörst', 'zu', 'kämpfen –'].map((w, i) => (
            <motion.span key={i}
              initial={{ opacity: 0, y: 60, filter: 'blur(20px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 1, delay: 0.5 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block mr-4 text-cream"
            >{w}</motion.span>
          ))}
          <br />
          {['und', 'anfängst', 'zu', 'leuchten?'].map((w, i) => (
            <motion.span key={i}
              initial={{ opacity: 0, y: 60, filter: 'blur(20px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 1, delay: 1.4 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block mr-4 text-transparent bg-clip-text bg-gold-gradient"
              style={{ filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.4))' }}
            >{w}</motion.span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 2.4 }}
          className="text-xl md:text-2xl font-serif italic text-cream/75 max-w-3xl mx-auto mb-6 leading-relaxed"
        >
          Du bist nicht zufällig hier. Deine Seele hat dich hergeführt –{' '}
          <span className="text-wine-light" style={{ textShadow: '0 0 15px rgba(155,66,78,0.4)' }}>
            weil sie weiß, dass jetzt der Moment ist.
          </span>
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 3 }}
          className="text-lg text-cream/55 max-w-2xl mx-auto mb-14 font-light leading-relaxed"
        >
          Bist du es leid, im Liebesleben auf eine Botschaft zu warten, die nicht kommt?
          Oder im Business mehr zu geben, als du hast – und trotzdem das Gefühl zu haben,
          nicht genug zu sein? Diese Masterclass ist dein Wendepunkt.
        </motion.p>

        <motion.a
          href="#anmeldung"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 3.4 }}
          whileHover={{ scale: 1.06, boxShadow: '0 0 70px rgba(212,175,55,0.8), 0 0 140px rgba(212,175,55,0.4)' }}
          whileTap={{ scale: 0.97 }}
          className="inline-block px-14 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer relative overflow-hidden group"
          style={{ boxShadow: '0 0 35px rgba(212,175,55,0.5)', borderRadius: '2px' }}
        >
          <span className="relative z-10">✦ Jetzt kostenlos anmelden →</span>
          <div className="absolute inset-0 bg-white/15 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
        </motion.a>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4 }}
          className="text-xs text-cream/30 mt-6 tracking-widest uppercase"
        >
          Begrenzte Plätze · Kein Replay garantiert
        </motion.p>
      </motion.div>

      <motion.div
        animate={{ y: [0, 14, 0], opacity: [0.2, 1, 0.2] }}
        transition={{ repeat: Infinity, duration: 3.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <div className="w-1 h-1 rounded-full bg-gold" style={{ boxShadow: '0 0 12px rgba(212,175,55,1)' }} />
        <div className="w-[1px] h-16 bg-gradient-to-b from-gold via-gold/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default HeroSection;